// src/lib/logoWhiten.js
//
// Client-side logo → white-on-transparent PNG conversion for the org-logo
// upload. Pure canvas, no ML matting. The returned blob IS the artifact that
// gets uploaded — the on-navy preview shows exactly these bytes.
//
// whitenLogo(file) → { blob (PNG), mode, warn }
//   mode 'already-white' → input was already white on transparency; re-encoded
//                          unchanged (do NOT recolor).
//   mode 'recolored'     → had alpha; RGB forced white, existing alpha kept
//                          (preserves anti-aliasing).
//   mode 'keyed'         → opaque input; the dominant corner colour is keyed out
//                          to alpha and RGB forced white. warn=true (verify it).

const MAX_EDGE = 512;
const ALPHA_OPAQUE = 250; // alpha >= this counts as non-transparent
const WHITE_MIN = 240;    // r,g,b all > this → "near white"
const CORNER_CLUSTER = 40; // corner colours within this distance are "the same"

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = url;
  });
}

// Rasterize a File (raster image or SVG) onto a canvas, capped at MAX_EDGE
// longest edge. SVGs with no intrinsic size are drawn into a 512-box.
async function rasterize(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    let w = img.naturalWidth || img.width || 0;
    let h = img.naturalHeight || img.height || 0;
    if (!w || !h) { w = MAX_EDGE; h = MAX_EDGE; }
    const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, cw, ch);
    return { canvas, ctx, cw, ch };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toPngBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

function colorDist(r1, g1, b1, r2, g2, b2) {
  const dr = r1 - r2, dg = g1 - g2, db = b1 - b2;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

// Dominant background colour = the corner whose colour the most corners cluster
// around (handles the case where one corner clips the logo).
function dominantCorner(corners) {
  let best = corners[0];
  let bestScore = -1;
  for (const c of corners) {
    let score = 0;
    for (const o of corners) {
      if (colorDist(c[0], c[1], c[2], o[0], o[1], o[2]) < CORNER_CLUSTER) score++;
    }
    if (score > bestScore) { bestScore = score; best = c; }
  }
  return best;
}

export async function whitenLogo(file) {
  const { canvas, ctx, cw, ch } = await rasterize(file);
  const imageData = ctx.getImageData(0, 0, cw, ch);
  const d = imageData.data;

  // ── Classify ──────────────────────────────────────────────────────────
  let hasAlpha = false;
  let opaqueCount = 0;
  let opaqueWhite = 0;
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a < ALPHA_OPAQUE) {
      hasAlpha = true;
    } else {
      opaqueCount++;
      if (d[i] > WHITE_MIN && d[i + 1] > WHITE_MIN && d[i + 2] > WHITE_MIN) opaqueWhite++;
    }
  }
  const alreadyWhite = opaqueCount > 0 && opaqueWhite / opaqueCount >= 0.95;

  // ── (a) already white → leave the pixels alone, just re-encode ────────
  if (alreadyWhite) {
    return { blob: await toPngBlob(canvas), mode: "already-white", warn: false };
  }

  // ── (b) has alpha → force RGB white, keep alpha (preserves AA) ────────
  if (hasAlpha) {
    for (let i = 0; i < d.length; i += 4) {
      d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    return { blob: await toPngBlob(canvas), mode: "recolored", warn: false };
  }

  // ── (c) opaque → key out the dominant corner colour to alpha ──────────
  const px = (x, y) => {
    const i = (y * cw + x) * 4;
    return [d[i], d[i + 1], d[i + 2]];
  };
  const bg = dominantCorner([
    px(0, 0), px(cw - 1, 0), px(0, ch - 1), px(cw - 1, ch - 1),
  ]);
  for (let i = 0; i < d.length; i += 4) {
    // distance 0 (== bg) → alpha 0 (transparent); far → opaque. clamp 0..255.
    const dist = colorDist(d[i], d[i + 1], d[i + 2], bg[0], bg[1], bg[2]);
    d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
    d[i + 3] = Math.max(0, Math.min(255, Math.round(dist)));
  }
  ctx.putImageData(imageData, 0, 0);
  return { blob: await toPngBlob(canvas), mode: "keyed", warn: true };
}
